import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useToast } from "@/components/ui/toast"
import {
  useCashRegistersQuery,
  useUpdateCashRegisterReaderIdMutation,
} from "@/query/cashRegisterQuery"
import { useStripeReadersQuery } from "@/query/stripeTerminalQuery"
import type { CashRegisterTable } from "@okeys/shared-utils"
import { Info, Link, Loader2, Plus, Wifi, WifiOff } from "lucide-react"
import React, { useState } from "react"

type AddTerminalToCashRegisterModalProps = {
  isOpen: boolean
  onClose: () => void
  onOpenManageReaders: () => void
  caisseId?: string
}

export const AddTerminalToCashRegisterModal: React.FC<
  AddTerminalToCashRegisterModalProps
> = ({ isOpen, onClose, onOpenManageReaders, caisseId }) => {
  const updateCashRegisterReaderIdMutation =
    useUpdateCashRegisterReaderIdMutation()
  const { addToast } = useToast()

  // États pour la sélection du terminal
  const [selectedReaderId, setSelectedReaderId] = useState<string>("")

  // Queries pour récupérer les données
  const { readers = [], isLoading: loadingReaders } = useStripeReadersQuery()
  const { cashRegisters, pendingCashRegisters: loadingCashRegisters } =
    useCashRegistersQuery()

  // Trouver la caisse courante
  const currentCashRegister = cashRegisters.find(
    (cr: CashRegisterTable) => cr.caisseId === caisseId
  )

  // Filtrer les terminaux disponibles
  const usedReaderIds = cashRegisters.map(
    (cr: CashRegisterTable) => cr.readerId
  )
  const availableReaders = readers.filter(
    (reader) => !usedReaderIds.includes(reader.id)
  )

  const handleAddTerminalToCashRegister = async () => {
    if (!caisseId) {
      addToast({
        type: "error",
        title: "Erreur",
        description: "ID de caisse manquant",
      })
      return
    }

    if (!selectedReaderId) {
      addToast({
        type: "error",
        title: "Terminal requis",
        description: "Veuillez sélectionner un terminal disponible",
      })
      return
    }

    try {
      await updateCashRegisterReaderIdMutation.mutateAsync({
        caisseId,
        readerId: selectedReaderId,
      })

      addToast({
        type: "success",
        title: "Terminal associé",
        description: "Le terminal a été associé à la caisse avec succès",
      })

      handleClose()
    } catch (error) {
      console.error("Erreur lors de l'association du terminal:", error)
      // L'erreur est déjà gérée par la mutation
    }
  }

  const handleClose = () => {
    if (!updateCashRegisterReaderIdMutation.isPending) {
      setSelectedReaderId("")
      onClose()
    }
  }

  const handleOpenManageReaders = () => {
    handleClose()
    onOpenManageReaders()
  }

  const getStatusIcon = (status: string) => {
    return status === "online" ? (
      <Wifi className="h-4 w-4 text-green-500" />
    ) : (
      <WifiOff className="h-4 w-4 text-red-500" />
    )
  }

  const getStatusLabel = (status: string) => {
    return status === "online" ? "En ligne" : "Hors ligne"
  }

  const isLoading =
    updateCashRegisterReaderIdMutation.isPending ||
    loadingReaders ||
    loadingCashRegisters

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Link className="h-5 w-5 text-indigo-600" />
            <DialogTitle>Ajouter un terminal à la caisse</DialogTitle>
          </div>
          <DialogDescription>
            Associez un terminal de paiement à la caisse "
            {currentCashRegister?.name ||
              currentCashRegister?.caisseId?.slice(0, 8) ||
              "Inconnue"}
            ".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Section Terminal */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Sélectionner un terminal</h3>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Chargement des terminaux...</span>
              </div>
            ) : availableReaders.length === 0 ? (
              <div className="text-center space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg justify-center items-center flex flex-col">
                  <Info className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                  <h4 className="font-medium text-blue-900 mb-2">
                    Aucun terminal disponible
                  </h4>
                  <p className="text-sm text-blue-700 mb-4">
                    Tous vos terminaux sont déjà associés à des caisses ou vous
                    n'avez pas encore de terminal enregistré.
                  </p>
                  <Button
                    onClick={handleOpenManageReaders}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Gérer les terminaux
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Sélectionnez un terminal disponible pour cette caisse :
                </p>

                {/* Terminaux disponibles */}
                <div className="space-y-2">
                  {availableReaders.map((reader) => (
                    <div
                      key={reader.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        selectedReaderId === reader.id
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => setSelectedReaderId(reader.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(reader.status || "")}
                          <div>
                            <p className="font-medium">{reader.label}</p>
                            <p className="text-sm text-gray-500">
                              {getStatusLabel(reader.status || "")} • N/S:{" "}
                              {reader.serial_number}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name="selectedReader"
                            value={reader.id}
                            checked={selectedReaderId === reader.id}
                            onChange={() => setSelectedReaderId(reader.id)}
                            className="h-4 w-4 text-indigo-600"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-yellow-600" />
                    <p className="text-sm text-yellow-800">
                      <strong>Note :</strong> Une fois associé, ce terminal ne
                      pourra pas être utilisé par d'autres caisses.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            onClick={handleAddTerminalToCashRegister}
            disabled={isLoading || !selectedReaderId}
            className="flex items-center gap-2"
          >
            {updateCashRegisterReaderIdMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Association en cours...
              </>
            ) : (
              <>
                <Link className="h-4 w-4" />
                Associer le terminal
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
